import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import AssigneePicker, { AssigneeChip } from "@/components/AssigneePicker";
import {
  Wrench, Plus, Trash2, Plug, Droplet, Trees, Paintbrush, Sofa, Wind, Refrigerator,
  Hammer, ChevronDown, ChevronUp, CheckCircle2, CircleDotDashed, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "Electrical",      icon: Plug },
  { key: "Plumbing",        icon: Droplet },
  { key: "Landscaping",     icon: Trees },
  { key: "Painting",        icon: Paintbrush },
  { key: "Furniture Repair", icon: Sofa },
  { key: "HVAC",            icon: Wind },
  { key: "Appliance",       icon: Refrigerator },
  { key: "Other",           icon: Hammer },
];

const PRIORITY_COLORS = {
  low: "bg-hearth-sage/15 text-hearth-sageDark",
  medium: "bg-hearth-amber/20 text-[#9c6c2c]",
  high: "bg-hearth-coral/15 text-hearth-coral",
};
const STATUS_COLORS = {
  open: "bg-hearth-coral/15 text-hearth-coral",
  in_progress: "bg-hearth-amber/20 text-[#9c6c2c]",
  done: "bg-hearth-sage/15 text-hearth-sageDark",
};

export default function Maintenance() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const blank = { title: "", category: "Electrical", priority: "medium", status: "open", notes: "", due_date: "", cost: "", completed_date: "", assignee_id: null };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    const [{ data }, mres] = await Promise.all([
      api.get("/maintenance"),
      api.get("/household/members").catch(() => ({ data: [] })),
    ]);
    setItems(data);
    setMembers(Array.isArray(mres.data) ? mres.data : (mres.data?.members || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const byCategory = filter === "All" ? items : items.filter((i) => i.category === filter);
  // Split into active vs done so the page never mingles "still to do" with "already finished".
  const active = byCategory.filter((i) => i.status !== "done");
  const done   = byCategory.filter((i) => i.status === "done");
  // Sort active by priority (high → low) then earliest due_date first.
  const priorityRank = { high: 0, medium: 1, low: 2 };
  active.sort((a, b) => {
    const p = (priorityRank[a.priority] ?? 3) - (priorityRank[b.priority] ?? 3);
    if (p !== 0) return p;
    return (a.due_date || "9999") < (b.due_date || "9999") ? -1 : 1;
  });
  // Done: newest completion first.
  done.sort((a, b) => (b.completed_date || "").localeCompare(a.completed_date || ""));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      const payload = { ...form, cost: form.cost ? parseFloat(form.cost) : null };
      if (editId) await api.put(`/maintenance/${editId}`, payload);
      else await api.post("/maintenance", payload);
      setOpen(false); setForm(blank); setEditId(null);
      toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (it) => { setForm({ ...blank, ...it, cost: it.cost ?? "" }); setEditId(it.id); setOpen(true); };
  const remove = async (id) => { await api.delete(`/maintenance/${id}`); load(); };

  return (
    <div>
      <PageHeader
        eyebrow="Repairs & upkeep"
        title="Maintenance"
        subtitle="Keep tabs on what's broken, leaking or due for a touch-up."
        action={
          <Button data-testid="maint-add-button" onClick={() => { setEditId(null); setForm(blank); setOpen(true); }}
            className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-6 h-11">
            <Plus size={16} className="mr-1.5" /> Add task
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        <button data-testid="maint-filter-All" onClick={() => setFilter("All")}
                className={`pill-btn text-sm border ${filter === "All" ? "bg-hearth-ink text-white border-hearth-ink" : "bg-hearth-paper border-hearth-line text-hearth-mute hover:text-hearth-ink"}`}>
          All
        </button>
        {CATEGORIES.map(({ key, icon: Icon }) => (
          <button key={key} data-testid={`maint-filter-${key}`} onClick={() => setFilter(key)}
            className={`pill-btn text-sm border inline-flex items-center gap-1.5 ${filter === key ? "bg-hearth-ink text-white border-hearth-ink" : "bg-hearth-paper border-hearth-line text-hearth-mute hover:text-hearth-ink"}`}>
            <Icon size={14} strokeWidth={1.6} /> {key}
          </button>
        ))}
      </div>

      {byCategory.length === 0 ? (
        <EmptyState icon={Wrench} title="Nothing to fix." message="When something breaks or needs servicing, log it here so it doesn't get forgotten." />
      ) : (
        <>
          {/* ACTIVE — up-next tasks: open + in_progress. Bright warm surface. */}
          {active.length > 0 && (
            <section className="mb-12" data-testid="maint-section-active">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-2xl bg-hearth-clay/15 text-hearth-clayDark grid place-items-center">
                    <CircleDotDashed size={16} strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="caption text-hearth-clayDark tracking-widest">Up next</div>
                    <h2 className="font-display text-2xl text-hearth-ink">
                      {active.length} {active.length === 1 ? "task" : "tasks"} to see through
                    </h2>
                  </div>
                </div>
              </div>
              <MaintenanceGrid items={active} members={members} onEdit={startEdit} onDelete={remove} tone="active" />
            </section>
          )}

          {/* DIVIDER — warm, editorial, whispered separation of active vs done */}
          {active.length > 0 && done.length > 0 && (
            <div className="flex items-center gap-4 mb-8" aria-hidden>
              <div className="h-px flex-1 bg-hearth-line" />
              <span className="caption text-hearth-mute tracking-widest">Finished</span>
              <div className="h-px flex-1 bg-hearth-line" />
            </div>
          )}

          {/* DONE — collapsed by default, sage-green tone, muted card style */}
          {done.length > 0 && (
            <section data-testid="maint-section-done">
              <button
                data-testid="maint-toggle-done"
                type="button"
                onClick={() => setShowDone((s) => !s)}
                className="w-full flex items-center justify-between px-5 py-4 rounded-2xl bg-hearth-sage/8 border border-hearth-sage/25 hover:bg-hearth-sage/15 transition mb-5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-2xl bg-hearth-sage/25 text-hearth-sageDark grid place-items-center">
                    <CheckCircle2 size={16} strokeWidth={1.6} />
                  </div>
                  <div className="text-left">
                    <div className="caption text-hearth-sageDark tracking-widest">Done and dusted</div>
                    <div className="font-display text-lg text-hearth-ink">
                      {done.length} finished task{done.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                {showDone ? <ChevronUp size={18} className="text-hearth-mute" /> : <ChevronDown size={18} className="text-hearth-mute" />}
              </button>
              {showDone && (
                <MaintenanceGrid items={done} members={members} onEdit={startEdit} onDelete={remove} tone="done" />
              )}
            </section>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editId ? "Edit task" : "New maintenance task"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="caption text-hearth-mute">Title</Label>
              <Input data-testid="maint-form-title" className="input-warm mt-1.5 h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Kitchen tap is leaking" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="maint-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.key}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption text-hearth-mute">Due date</Label>
                <Input data-testid="maint-form-duedate" type="date" className="input-warm mt-1.5 h-11" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption text-hearth-mute">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In progress</SelectItem><SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Cost (paid / quoted)</Label>
                <Input data-testid="maint-form-cost" type="number" step="0.01" className="input-warm mt-1.5 h-11"
                       value={form.cost ?? ""} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <Label className="caption text-hearth-mute">Completed on</Label>
                <Input type="date" className="input-warm mt-1.5 h-11"
                       value={form.completed_date || ""} onChange={(e) => setForm({ ...form, completed_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="caption text-hearth-mute">Notes</Label>
              <Textarea data-testid="maint-form-notes" className="input-warm mt-1.5" rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <AssigneePicker
              value={form.assignee_id}
              onChange={(id) => setForm({ ...form, assignee_id: id })}
              label="Assign to"
              testId="maint-form-assignee"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="maint-form-save" onClick={save} className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The card grid used for both "Up next" and "Done" sections. Same layout;
 * ``tone`` swaps the card treatment so finished tasks feel visually
 * settled (muted, low elevation) while active tasks feel current.
 */
function MaintenanceGrid({ items, members, onEdit, onDelete, tone = "active" }) {
  const isDone = tone === "done";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((it) => {
        const Icon = CATEGORIES.find((c) => c.key === it.category)?.icon || Hammer;
        return (
          <div
            key={it.id}
            data-testid={`maint-card-${it.id}`}
            className={`warm-card p-6 fade-up flex flex-col transition-transform hover:-translate-y-0.5 ${
              isDone ? "opacity-85 bg-hearth-sage/5 border-hearth-sage/20" : ""
            }`}
          >
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-2xl grid place-items-center ${
                isDone
                  ? "bg-hearth-sage/20 text-hearth-sageDark"
                  : "bg-hearth-terracotta/12 text-hearth-terracotta"
              }`}>
                <Icon size={18} strokeWidth={1.6} />
              </div>
              <div className="flex gap-1.5 flex-wrap justify-end">
                {!isDone && (
                  <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${PRIORITY_COLORS[it.priority]}`}>
                    {it.priority}
                  </span>
                )}
                <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${STATUS_COLORS[it.status]}`}>
                  {it.status.replace("_", " ")}
                </span>
              </div>
            </div>
            <h3 className={`font-display text-xl font-semibold mt-4 ${
              isDone ? "text-hearth-mute line-through decoration-hearth-sage/40" : "text-hearth-ink"
            }`}>{it.title}</h3>
            <p className="caption text-hearth-mute mt-1">{it.category}</p>
            {it.notes && <p className="text-sm text-hearth-mute mt-3 leading-relaxed">{it.notes}</p>}
            <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
              {it.cost != null && it.cost > 0 && (
                <span className="chip bg-hearth-clay/12 text-hearth-clayDark font-mono" data-testid={`maint-cost-${it.id}`}>
                  {money(it.cost)}
                </span>
              )}
              {it.due_date && !isDone && (
                <span className="inline-flex items-center gap-1 text-hearth-mute">
                  <AlertCircle size={11} /> Due {fmtDate(it.due_date)}
                </span>
              )}
              {it.completed_date && (
                <span className="inline-flex items-center gap-1 text-hearth-sageDark">
                  <CheckCircle2 size={11} /> Done {fmtDate(it.completed_date)}
                </span>
              )}
              <AssigneeChip assigneeId={it.assignee_id} members={members} />
            </div>
            <div className="mt-auto pt-4 flex gap-2">
              <Button variant="ghost" data-testid={`maint-edit-${it.id}`} onClick={() => onEdit(it)} className="rounded-full text-sm">Edit</Button>
              <Button variant="ghost" data-testid={`maint-delete-${it.id}`} onClick={() => onDelete(it.id)} className="rounded-full text-sm text-hearth-coral hover:bg-hearth-coral/10">
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
